import { IServerResponse } from './types/interfaces/Api';
import { ISlotViewItem } from './types/interfaces/Slots/Slots';
import { slotProvidersConfiguration } from './configuration';
import { IGetGameUrl } from './types/slot';
import { QueryTypes } from './types/enums/api';
import BaseApi from 'services/api/BaseApi';

interface IGetGameList {
  provider: string;
  extraData?: unknown;
  platform?: 'desktop' | 'mobile';
  method?: QueryTypes;
}

interface IGetExtraData {
  url: string;
  // eslint-disable-next-line no-unused-vars
  converter: (data: unknown) => unknown;
}

interface IGetSessionUrl {
  provider: string;
  gameId: string | number;
  params: object;
  extraData: object;
  gameList: ISlotViewItem[];
}

interface ISlotApi {
  // eslint-disable-next-line no-unused-vars
  getGameList({ provider }: IGetGameList): Promise<IServerResponse<ISlotViewItem[]>>;
  // eslint-disable-next-line no-unused-vars
  getExtraData({ url, converter }: IGetExtraData): Promise<IServerResponse<unknown>>;
  // eslint-disable-next-line no-unused-vars
  getSessionUrl({ provider, gameId, params, extraData, gameList }: IGetSessionUrl): Promise<IServerResponse<string>>;
}

const substituteCurrencyProviders = ['KaGaming', 'DLV'].map(item => item.toLowerCase());

export class CasinoApi extends BaseApi implements ISlotApi {
  constructor(baseUrl = '') {
    super();
    this.baseUrl = baseUrl;
  }

  getGameList({ provider, extraData, platform }: IGetGameList): Promise<IServerResponse<ISlotViewItem[]>> {
    const {
      gameListUrl,
      converter,
      method = QueryTypes.GET,
      params = {},
      getBody = () => ({}),
    } = slotProvidersConfiguration[provider];
    const { sessionUrlData: { getHeaders } = {} } = slotProvidersConfiguration[provider];
    const { gameListUrlData: { getQuery } = {} } = slotProvidersConfiguration[provider];

    const headers = getHeaders ? getHeaders({ ...params }) : undefined;
    const body = getBody({ platform });
    const query = getQuery ? `?${getQuery({ platform })}` : '';
    if (gameListUrl) {
      return this.sendQuery(method, `${this.baseUrl}/${gameListUrl}${query}`, body, headers, data =>
        converter.convertGameList({ data, extraData, platform }),
      );
    }

    return new Promise(resolve => {
      resolve({
        success: true,
        data: converter?.convertGameList({ data: [] as any, extraData, platform }) ?? [],
        errorMessage: '',
        codeStatus: '',
      });
    });
  }

  getExtraData({ url, converter }: IGetExtraData): Promise<IServerResponse<unknown>> {
    return this.sendQuery(this.queryTypes.GET, `${this.baseUrl}/${url}`, undefined, undefined, converter);
  }

  getSessionUrl({
    provider,
    gameId,
    params = {},
    extraData = {},
    gameList,
  }: IGetSessionUrl): Promise<IServerResponse<string>> {
    const { sessionUrlData: { url, converter, getBody, getHeaders, getQuery, method } = {} } =
      slotProvidersConfiguration[provider];
    const body = getBody ? getBody({ gameId, ...params }) : undefined;
    const headers = getHeaders ? getHeaders({ ...params }) : undefined;

    const isCurrencySubstitute =
      provider === 'slotegrator'
        ? substituteCurrencyProviders.some(
            item => item === gameList.find(item1 => item1.gameId === gameId)?.provider.toLowerCase(),
          )
        : false;

    const urlQuery = getQuery ? getQuery({ isCurrencySubstitute, gameId, ...params }) : '';
    if (url) {
      return this.sendQuery(method || this.queryTypes.POST, `${this.baseUrl}/${url}${urlQuery}`, body, headers, data =>
        converter
          ? converter({
              data,
              extraData,
              gameId,
              gameList,
              lang: CasinoApi.lang,
              params,
            } as IGetGameUrl<unknown, unknown>)
          : data,
      );
    }
    return new Promise(resolve => {
      resolve({
        success: true,
        data: converter
          ? converter({
              data: {},
              extraData,
              gameId,
              gameList,
              lang: CasinoApi.lang,
              params,
            } as IGetGameUrl<unknown, unknown>)
          : '',
        errorMessage: '',
        codeStatus: '',
      });
    });
  }
}
